// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Settings page components
import FormField from "layouts/pages/account/components/FormField";

// Data
import {useRef, useState} from "react";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import {useEffect} from "react";
import ClientsService from "../../../../../services/clients.service";
import AmenitiesService from "../../../../../services/amenities.service";
import AuthService from "../../../../../services/auth.service";

function BookingData({values, setValues, refreshAmenityForUser, ...other}) {

    const [checked, setChecked ] = useState(Boolean(values.for_other));
    const [buildings, setBuildings] = useState([]);
    const [memberOptions, setMemberOptions] = useState([]);
    const [memberQuery, setMemberQuery] = useState("");
    const canBookForOther = AuthService.getUserRole() !== "member";
    const defaultInputValues = {
        completeName: values.completeName || '',
        email: values.email || '',
        phone: values.phone || '',
        building: values.selected_building || null,
        member: values.selected_member || null,
        minorsCount:  '0',
        guestsCount: '0',
      };

    const [insideValues, setInsideValues] = useState(defaultInputValues);
    const selectionKey = `${values.selected_building?.id || ''}:${values.selected_member?.id || ''}`;
    const previousSelectionKey = useRef(selectionKey);

      useEffect(() => {
          if (previousSelectionKey.current === selectionKey) return;
          previousSelectionKey.current = selectionKey;
          const member = values.selected_member || null;
          setChecked(Boolean(values.for_other));
          setInsideValues((current) => ({
              ...current,
              building: values.selected_building || null,
              member,
              completeName: member?.name || values.completeName || '',
              email: member?.email || values.email || '',
              phone: member?.phone || values.phone || '',
          }));
      }, [selectionKey, values.selected_member, values.selected_building, values.for_other,
          values.completeName, values.email, values.phone]);

      useEffect(() => {
          ClientsService.getBuildings()
            .then((response) => setBuildings(response.data || []))
            .catch(() => setBuildings([]));
      }, []);

      useEffect(() => {
          if (!checked || !insideValues.building?.id) {
              setMemberOptions([]);
              return;
          }
          AmenitiesService.searchBookingMembers(values.id, insideValues.building.id, memberQuery)
            .then((response) => setMemberOptions(response.data?.members || []))
            .catch(() => setMemberOptions([]));
      }, [checked, insideValues.building, memberQuery, values.id]);

      useEffect(() => {
          if (!canBookForOther) {
              setChecked(false);
              setValues({...values, for_other:false, isValid:true});
              return;
          }
          setValues({...values, isValid: checked ? Boolean(insideValues.member?.id) : true});
      }, [checked, insideValues.member]);


    const handleMemberSelected = (member) => {
        const nextValues = {
            ...insideValues,
            member,
            completeName: member?.name || "",
            email: member?.email || "",
            phone: member?.phone || "",
        };
        setInsideValues(nextValues);
        setValues({
            ...values,
            for_other: true,
            selected_building: nextValues.building,
            selected_member: member,
            user_id: member?.id || 0,
            completeName: member?.name || "",
            email: member?.email || "",
            phone: member?.phone || "",
            isValid: Boolean(member?.id),
        });
        if (member?.id && refreshAmenityForUser) {
            refreshAmenityForUser(member.id, {
                for_other: true,
                selected_building: nextValues.building,
                selected_member: member,
                user_id: member.id,
                completeName: member.name || "",
                email: member.email || "",
                phone: member.phone || "",
            });
        }
    };
    let list = [];
    const renderMinors = () => {
        if(values.minors_count > 0){
            list = []
            for (let i = 0; i <= values.minors_count; i++) {
                list.push(i);
            }
            return(<Grid item xs={12} md={6}><Autocomplete
                      defaultValue="0"
                      options={list}
                      getOptionLabel={(option) => option.toString() || 0}
                      onChange={(e,v)=>{
                            setValues({...values,  minors: parseInt(v)})
                      }}
                      renderInput={(params) => (
                        <FormField {...params} label="Minors Count" InputLabelProps={{ shrink: true }} />
                      )}
                    /></Grid>)
        }
        return ""
    }

    const renderGusts = () => {
        if(values.guests_count > 0){
            list = []
            for (let i = 0; i <= values.guests_count; i++) {
                list.push(i);
            }
            return(<Grid item xs={12} md={6}><Autocomplete
                      defaultValue="0"
                      options={list}
                      getOptionLabel={(option) => option.toString() || 0}
                      onChange={(e,v)=>{
                          setValues({...values,  guests: parseInt(v)})
                      }}
                      renderInput={(params) => (
                        <FormField {...params} label="Guests Count" InputLabelProps={{ shrink: true }} />
                      )}
                    /></Grid>)
        }
        return ""
    }
    const checkChange = (event) =>{
        if(!event.target.checked){
            setInsideValues({...insideValues, completeName: "", email: "", phone: "", building: null, member: null});
            setValues({
                ...values,
                for_other: false,
                selected_building: null,
                selected_member: null,
                user_id: 0,
                completeName: "",
                email: "",
                phone: "",
                isValid: true,
            });
            if (refreshAmenityForUser) {
                refreshAmenityForUser(null, {
                    for_other: false,
                    selected_building: null,
                    selected_member: null,
                    user_id: 0,
                });
            }
        } else {
            setValues({...values, for_other: true, isValid: false});
        }
        setChecked(event.target.checked)
    }

    return (
        <Card id="basic-info" sx={{ overflow: "visible" }}>
          <MDBox p={3}>
            <MDTypography variant="h5">Reserve Information</MDTypography>
          </MDBox>
          <MDBox component="form" pb={3} px={3}>
            <Grid container spacing={3}>

              {canBookForOther && <Grid item xs={12} md={12}>
                  <Checkbox checked={checked} onChange={checkChange} />
                  <MDTypography variant="button" fontWeight="regular"
                                color="text" sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}>
                      &nbsp;&nbsp;Book for someone else&nbsp;</MDTypography>
              </Grid>
              }
              <Grid item xs={12} md={4} sx={{"display":checked ? "flex": "none"}}>
                  <Autocomplete
                    fullWidth
                    sx={{ width: "100%" }}
                    options={buildings}
                    value={insideValues.building}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option?.name || ""}
                    onChange={(event, value) => {
                        const nextValues = { ...insideValues, building: value, member: null, completeName: "", email: "", phone: "" };
                        setInsideValues(nextValues);
                        setValues({...values, selected_building: value, selected_member: null, user_id: 0, isValid: false});
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Building" required variant="standard" InputLabelProps={{ shrink: true }} />
                    )}
                  />
              </Grid>
              <Grid item xs={12} md={8} sx={{"display":checked ? "flex": "none"}}>
                  <Autocomplete
                    fullWidth
                    sx={{ width: "100%" }}
                    options={insideValues.member
                        ? [insideValues.member, ...memberOptions.filter((option) => option.id !== insideValues.member.id)]
                        : memberOptions}
                    value={insideValues.member}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={!insideValues.building}
                    filterOptions={(options) => options}
                    getOptionLabel={(option) => {
                        if (!option) return "";
                        return `${option.unit || "-"} - ${option.name || ""}`;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <MDBox>
                          <MDTypography variant="button" fontWeight="medium">
                            Unit {option.unit || "-"} - {option.name || "-"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" display="block">
                            {option.building || "-"} | {option.email || "-"} | {option.phone || "-"}
                          </MDTypography>
                        </MDBox>
                      </li>
                    )}
                    onInputChange={(event, value) => setMemberQuery(value)}
                    onChange={(event, value) => handleMemberSelected(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Search by unit or resident name" required variant="standard" InputLabelProps={{ shrink: true }} />
                    )}
                  />
              </Grid>
              {checked && insideValues.member && (
                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    Building: {insideValues.member.building || "-"} &nbsp; | &nbsp;
                    Unit: {insideValues.member.unit || "-"} &nbsp; | &nbsp;
                    Name: {insideValues.member.name || "-"} &nbsp; | &nbsp;
                    Email: {insideValues.member.email || "-"} &nbsp; | &nbsp;
                    Phone: {insideValues.member.phone || "-"}
                  </MDTypography>
                </Grid>
              )}
                {renderMinors()}
                {renderGusts()}
            </Grid>
          </MDBox>
        </Card>
    );
}

export default BookingData;
